'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '../lib/supabase/client';
import { logAuditEvent } from '../lib/auditLog';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Clears all Supabase auth tokens from localStorage and cookies
const clearStaleTokens = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith('sb-') || k.startsWith('sb_'))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}
  try {
    document.cookie.split(';').forEach((c) => {
      const name = c.trim().split('=')[0];
      if (name.startsWith('sb-') || name.startsWith('sb_')) {
        document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`;
        document.cookie = `${name}=; Path=/; Max-Age=0`;
      }
    });
  } catch {}
  // Do NOT reset the singleton here — destroying it while the existing client
  // holds a Web Lock causes "AbortError: Lock broken by another request with the 'steal' option"
};

const isRefreshTokenError = (error: any): boolean => {
  if (!error) return false;
  const msg = (error.message || error.error_description || '').toLowerCase();
  return (
    msg.includes('refresh token not found') ||
    msg.includes('invalid refresh token') ||
    msg.includes('refresh_token_not_found') ||
    msg.includes('token has expired') ||
    error.code === 'refresh_token_not_found' ||
    (error.__isAuthError === true && msg.includes('refresh'))
  );
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [staffProfile, setStaffProfile] = useState<any>(null);
  const loadingProfileRef = useRef(false);
  const supabaseRef = useRef(createClient());
  const router = useRouter();

  const resetAuthState = () => {
    setSession(null);
    setUser(null);
    setStaffProfile(null);
    setLoading(false);
  };

  const handleStaleToken = async () => {
    clearStaleTokens();
    // Do NOT recreate the client here — creating a new client while the existing one
    // holds a Web Lock causes "AbortError: Lock broken by another request with the 'steal' option".
    // Reuse the existing client instance to sign out gracefully.
    try {
      await supabaseRef.current.auth.signOut({ scope: 'local' });
    } catch {}
    resetAuthState();
    // Redirect to login so user can re-authenticate
    router.replace('/sign-up-login-screen');
  };

  // Load staff profile for a given auth user
  const loadStaffProfile = async (authUser: any) => {
    if (!authUser) {
      setStaffProfile(null);
      return;
    }
    if (loadingProfileRef.current) return;
    loadingProfileRef.current = true;
    try {
      const { data } = await supabaseRef.current
        .from('staff_profiles')
        .select('id, full_name, role, email')
        .eq('user_id', authUser.id)
        .single();
      setStaffProfile(data || null);
    } catch {
      setStaffProfile(null);
    } finally {
      loadingProfileRef.current = false;
    }
  };

  useEffect(() => {
    let subscriptionCleanup: (() => void) | null = null;
    let isMounted = true;

    // Catch background token-refresh failures that Supabase emits as
    // unhandled promise rejections (autoRefreshToken: true path).
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (!isMounted) return;
      if (isRefreshTokenError(event.reason)) {
        event.preventDefault();
        handleStaleToken();
      }
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    const initialize = async () => {
      const supabase = supabaseRef.current;

      // Set up auth state change listener first
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange(async (event, newSession) => {
        if (!isMounted) return;

        // TOKEN_REFRESHED with no session means the refresh token was invalid
        if (event === 'TOKEN_REFRESHED' && !newSession) {
          await handleStaleToken();
          return;
        }
        // TOKEN_REFRESH_FAILED — refresh token expired/invalid
        if ((event as string) === 'TOKEN_REFRESH_FAILED') {
          await handleStaleToken();
          return;
        }
        // Handle sign out — if no session exists after sign-out it may be
        // a forced sign-out triggered by an invalid refresh token; redirect.
        if (event === 'SIGNED_OUT') {
          resetAuthState();
          router.replace('/sign-up-login-screen');
          return;
        }

        setSession(newSession);
        setUser(newSession?.user ?? null);
        loadStaffProfile(newSession?.user ?? null).finally(() => {
          if (isMounted) setLoading(false);
        });
      });

      subscriptionCleanup = () => subscription.unsubscribe();

      // Now check the current session
      try {
        const {
          data: { session: initialSession },
          error,
        } = await supabase.auth.getSession();

        if (!isMounted) return;

        if (error && isRefreshTokenError(error)) {
          await handleStaleToken();
          return;
        }

        // If no session, mark loading done (onAuthStateChange INITIAL_SESSION may not fire)
        if (!initialSession) {
          setLoading(false);
        }
        // If there is a session, onAuthStateChange INITIAL_SESSION will handle it
      } catch (err: any) {
        if (!isMounted) return;
        if (isRefreshTokenError(err)) {
          await handleStaleToken();
        } else {
          setLoading(false);
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
      subscriptionCleanup?.();
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabaseRef.current.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabaseRef.current.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      throw error;
    }
    setTimeout(() => {
      logAuditEvent({
        action: 'Signed in via email/password',
        module: 'Authentication',
        details: { email },
      });
    }, 500);
    return data;
  };

  // Sign Out
  const signOut = async () => {
    await logAuditEvent({ action: 'Signed out', module: 'Authentication' });
    const { error } = await supabaseRef.current.auth.signOut();
    if (error) throw error;
  };

  // Google OAuth Sign In
  const signInWithGoogle = async () => {
    const { data, error } = await supabaseRef.current.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    if (data?.url) {
      window.location.href = data.url;
    }
    return data;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const {
      data: { user: currentUser },
      error,
    } = await supabaseRef.current.auth.getUser();
    if (error) throw error;
    return currentUser;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabaseRef.current
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Role helpers
  const isAgent = staffProfile?.role === 'agent';
  const isAdminOrManager =
    staffProfile?.role === 'admin' || staffProfile?.role === 'office_manager';

  const value = {
    user,
    session,
    loading,
    staffProfile,
    isAgent,
    isAdminOrManager,
    signUp,
    signIn,
    signOut,
    signInWithGoogle,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
